import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { search, getSession } from '../api/client'
import type { SearchResults, SessionSearchHit, LineSearchHit, AuditSearchHit } from '../types'
import { useStore } from '../store'

const DEBOUNCE_MS = 300
const MIN_QUERY_LEN = 2

export function GlobalSearch() {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [includeXml, setIncludeXml] = useState(false)
  const [results, setResults] = useState<SearchResults | null>(null)
  const [loading, setLoading] = useState(false)
  const [navigating, setNavigating] = useState(false)
  const [open, setOpen] = useState(false)
  const { setActiveView, setCurrentSession } = useStore()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])

  function handleChange(q: string) {
    setQuery(q)
    clearTimeout(debounceRef.current)
    if (q.length < MIN_QUERY_LEN) {
      setResults(null)
      setOpen(false)
      return
    }
    debounceRef.current = setTimeout(() => doSearch(q, includeXml), DEBOUNCE_MS)
  }

  async function doSearch(q: string, xml: boolean) {
    setLoading(true)
    try {
      const res = await search(q, xml)
      setResults(res)
      setOpen(true)
    } catch {
      // silently ignore search errors — geen modal nodig voor een zoekvraag
    } finally {
      setLoading(false)
    }
  }

  async function openSession(sessionId: number) {
    setNavigating(true)
    setOpen(false)
    setQuery('')
    try {
      const session = await getSession(sessionId)
      setCurrentSession(session)
      setActiveView('result')
    } catch {
      setActiveView('history')
    } finally {
      setNavigating(false)
    }
  }

  function handleXmlToggle(checked: boolean) {
    setIncludeXml(checked)
    if (query.length >= MIN_QUERY_LEN) {
      clearTimeout(debounceRef.current)
      doSearch(query, checked)
    }
  }

  const totalHits = results
    ? results.sessions.length + results.lines.length + results.audit_entries.length + results.xml_matches.length
    : 0

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <svg
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none"
          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
        >
          <circle cx="11" cy="11" r="8" />
          <path strokeLinecap="round" d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="search"
          value={query}
          onChange={e => handleChange(e.target.value)}
          onFocus={() => results && query.length >= MIN_QUERY_LEN && setOpen(true)}
          placeholder={t('global_search.placeholder')}
          className="w-52 text-sm bg-gray-100 dark:bg-gray-800 rounded-lg pl-8 pr-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-300 dark:focus:ring-blue-400 focus:bg-white dark:focus:bg-gray-700 dark:text-gray-100 transition-colors"
        />
        {(loading || navigating) && (
          <div className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
        )}
      </div>

      {open && results && (
        <div className="absolute right-0 top-full mt-2 w-[500px] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50 dark:bg-gray-900">
            <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {totalHits === 0
                ? t('global_search.no_results')
                : t('global_search.result_count', { count: totalHits })}
            </span>
            <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeXml}
                onChange={e => handleXmlToggle(e.target.checked)}
                className="rounded border-gray-300"
              />
              {t('global_search.also_search_xml')}
            </label>
          </div>

          <div className="max-h-[440px] overflow-y-auto">
            {results.sessions.length > 0 && (
              <ResultSection title={t('global_search.section_sessions')} count={results.sessions.length}>
                {results.sessions.map(s => (
                  <SessionHitRow key={s.id} hit={s} onClick={() => openSession(s.id)} />
                ))}
              </ResultSection>
            )}

            {results.lines.length > 0 && (
              <ResultSection title={t('global_search.section_line_descriptions')} count={results.lines.length}>
                {results.lines.map(l => (
                  <LineHitRow
                    key={`${l.session_id}-${l.line_id}`}
                    hit={l}
                    onClick={() => openSession(l.session_id)}
                  />
                ))}
              </ResultSection>
            )}

            {results.audit_entries.length > 0 && (
              <ResultSection title={t('global_search.section_audit_log')} count={results.audit_entries.length}>
                {results.audit_entries.map(a => (
                  <AuditHitRow
                    key={a.id}
                    hit={a}
                    onClick={() => { setActiveView('audit-log'); setOpen(false); setQuery('') }}
                  />
                ))}
              </ResultSection>
            )}

            {results.xml_matches.length > 0 && (
              <ResultSection title={t('global_search.section_xml_content')} count={results.xml_matches.length}>
                {results.xml_matches.map(s => (
                  <SessionHitRow
                    key={`xml-${s.id}`}
                    hit={s}
                    onClick={() => openSession(s.id)}
                    badge="XML"
                  />
                ))}
              </ResultSection>
            )}

            {totalHits === 0 && (
              <div className="px-4 py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                {t('global_search.no_results_for', { query })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

function ResultSection({
  title,
  count,
  children,
}: {
  title: string
  count: number
  children: React.ReactNode
}) {
  return (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <div className="px-4 py-1.5 bg-gray-50 dark:bg-gray-900 flex items-center justify-between">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{title}</span>
        <span className="text-xs text-gray-400 dark:text-gray-500">{count}</span>
      </div>
      <div>{children}</div>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    OK: 'bg-green-100 text-green-700',
    INFO: 'bg-blue-100 text-blue-700',
    WARNING: 'bg-yellow-100 text-yellow-700',
    ERROR: 'bg-red-100 text-red-700',
    UNKNOWN: 'bg-gray-100 text-gray-500',
  }
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${colors[status] ?? 'bg-gray-100 text-gray-500'}`}>
      {status}
    </span>
  )
}

function SessionHitRow({
  hit,
  onClick,
  badge,
}: {
  hit: SessionSearchHit
  onClick: () => void
  badge?: string
}) {
  const { t } = useTranslation()
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-start gap-3"
    >
      <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
      </svg>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{hit.supplier_name ?? '—'}</span>
          {hit.project_number && (
            <span className="text-xs text-gray-500 dark:text-gray-400">· {t('global_search.project_number', { number: hit.project_number })}</span>
          )}
          <StatusBadge status={hit.overall_status} />
          {badge && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-600">
              {badge}
            </span>
          )}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-2">
          <span className="font-mono text-gray-300 dark:text-gray-600">#{hit.id}</span>
          <span>{new Date(hit.created_at).toLocaleDateString('nl-NL')}</span>
          {hit.invoice_number && <span>{t('global_search.invoice_number', { number: hit.invoice_number })}</span>}
        </div>
      </div>
    </button>
  )
}

function LineHitRow({ hit, onClick }: { hit: LineSearchHit; onClick: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-start gap-3"
    >
      <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75a1.875 1.875 0 0 1 0 3.75H5.625a1.875 1.875 0 0 1 0-3.75Z" />
      </svg>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          <span className="font-mono">{hit.article_number}</span>
          {hit.description && (
            <span className="text-gray-600 dark:text-gray-400 font-normal ml-1.5">— {hit.description}</span>
          )}
        </div>
        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-gray-300 dark:text-gray-600">#{hit.session_id}</span>
          <span>{new Date(hit.session_date).toLocaleDateString('nl-NL')}</span>
          {hit.supplier_name && <span>{hit.supplier_name}</span>}
          {hit.project_number && <span>{t('global_search.project_number', { number: hit.project_number })}</span>}
          {hit.status !== 'OK' && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-700">
              {hit.status}
            </span>
          )}
        </div>
      </div>
    </button>
  )
}

function AuditHitRow({ hit, onClick }: { hit: AuditSearchHit; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors flex items-start gap-3"
    >
      <svg className="w-4 h-4 text-gray-300 dark:text-gray-600 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25Z" />
      </svg>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
          <span>{hit.username}</span>
          <span className="text-gray-400 dark:text-gray-500 mx-1">·</span>
          <span className="text-gray-600 dark:text-gray-400">{hit.action}</span>
        </div>
        {hit.details && (
          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{hit.details}</div>
        )}
        <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
          {new Date(hit.timestamp).toLocaleString('nl-NL')}
        </div>
      </div>
    </button>
  )
}
